'use client';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { applyProductFilter } from './applyProductFilter';
import {
  computeAvailability,
  type FilterAvailability,
} from './computeAvailability';
import { FilteredLinkGate } from './FilteredLinkGate';
import { filterQueryParams } from './filterQueryParams';
import type {
  FilterableProduct,
  FilterCategory,
  FilterSelection,
} from './productFilterTypes';
import { useProductFilter } from './useProductFilter';

type ProductFilterContextValue = {
  /**
   * The groups the page renders, already resolved: derived options filled in
   * and a group with none dropped. One list for the panel and the blocks, for
   * the same reason the sort config is single — two copies can disagree about
   * which options exist, and then the control offers a filter that matches
   * nothing.
   */
  categories: readonly FilterCategory[];
  selection: FilterSelection;
  /**
   * Which values would still return a product, per group. `undefined` means
   * "not computed" — the page could not enumerate its products, or a quote is
   * still running — and nothing is greyed (CMS-1542 R12.3.4).
   */
  availability?: FilterAvailability;
  toggleValue: (categoryId: string, value: string) => void;
  clearAll: () => void;
  activeCount: number;
  /**
   * How many products survive the current filters. `undefined` for the same
   * reason `availability` is: the page could not enumerate its products, so a
   * count it cannot substantiate must not be shown (R12.3).
   *
   * An empty `products` is not that case — it is an enumerated set that holds
   * nothing, and it counts 0 so the empty-state copy can render. A travel page
   * whose quote priced no plan arrives here (CMS-1542).
   */
  resultCount?: number;
  /**
   * The products that survive the current filters — what `resultCount`
   * counts, for a caller that needs more than the number (the eligibility
   * half of the results line, CMS-1905). `undefined` exactly when
   * `resultCount` is.
   */
  results?: readonly FilterableProduct[];
  /**
   * How many filter changes the reader has made through `toggleValue` /
   * `clearAll`: a count rather than a callback, so a consumer reacts after
   * the commit (CMS-2073).
   */
  filterChanges: number;
};

const EMPTY: ProductFilterContextValue = {
  categories: [],
  selection: {},
  toggleValue: () => undefined,
  clearAll: () => undefined,
  activeCount: 0,
  filterChanges: 0,
};

const ProductFilterContext = createContext<ProductFilterContextValue>(EMPTY);

/**
 * The table and the cards are separate blocks in the dynamic zone, so neither
 * can see the panel's groups alone. Resolved once upstream and published here,
 * which is what keeps the control and the results in step.
 */
export const ProductFilterProvider: React.FC<{
  categories?: readonly FilterCategory[];
  /**
   * The page's products, for availability and the count. Omitted where the
   * caller cannot enumerate them, which leaves every value selectable rather
   * than greying the lot. Passing an empty array says the opposite — they were
   * enumerated and there are none.
   */
  products?: readonly FilterableProduct[];
  getProduct?: (item: FilterableProduct) => FilterableProduct | undefined;
  children: React.ReactNode;
}> = ({ categories, products, getProduct, children }) => {
  const resolved = useMemo(() => categories ?? [], [categories]);
  const {
    selection,
    toggleValue: toggle,
    clearAll: clear,
    activeCount,
    applied,
  } = useProductFilter(resolved);
  const params = useMemo(() => filterQueryParams(resolved), [resolved]);

  // Only the reader's own changes count: a shared link and a trip details
  // change reach the selection through the URL, and scroll nothing.
  const [filterChanges, setFilterChanges] = useState(0);
  const toggleValue = useCallback(
    (categoryId: string, value: string) => {
      toggle(categoryId, value);
      setFilterChanges((count) => count + 1);
    },
    [toggle],
  );
  const clearAll = useCallback(() => {
    clear();
    // Clearing with nothing on is no change (the panel's Clear All shows
    // whenever it has groups).
    if (activeCount > 0) setFilterChanges((count) => count + 1);
  }, [clear, activeCount]);

  const availability = useMemo(
    () => computeAvailability(resolved, products, selection, getProduct),
    [resolved, products, selection, getProduct],
  );

  const results = useMemo(
    () =>
      products
        ? applyProductFilter(products, resolved, selection, getProduct)
        : undefined,
    [resolved, products, selection, getProduct],
  );
  const resultCount = results?.length;

  const value = useMemo(
    () => ({
      categories: resolved,
      selection,
      availability,
      toggleValue,
      clearAll,
      activeCount,
      resultCount,
      results,
      filterChanges,
    }),
    [
      resolved,
      selection,
      availability,
      toggleValue,
      clearAll,
      activeCount,
      resultCount,
      results,
      filterChanges,
    ],
  );

  return (
    <ProductFilterContext.Provider value={value}>
      <FilteredLinkGate params={params} applied={applied} />
      {children}
    </ProductFilterContext.Provider>
  );
};

/** Empty when the page has no panel, so blocks render their full product set. */
export const useProductFilterState = (): ProductFilterContextValue =>
  useContext(ProductFilterContext);
